import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, Users, ArrowRight, CheckCircle, User, Video } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { sessions } from '../data/sessions';
import { sessionDetails } from '../data/sessionDetails';

const SessionDetailPage = () => {
  const { id } = useParams();
  const { theme } = useTheme();
  const session = sessions.find(s => s.id === parseInt(id));
  const details = sessionDetails[id];

  if (!session || !details) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[var(--text-main)] mb-4">Session Not Found</h1>
          <Link to="/services/live-sessions" className="text-[var(--primary)] hover:underline">Back to Sessions</Link>
        </div>
      </div>
    );
  }

  const handleRegister = () => {
    toast.success(`Registered for ${session.title}`, { description: 'Check your email for the session link.' });
  };

  return (
    <div className="min-h-screen pt-20" data-testid="session-detail-page">
      <Toaster position="top-right" theme={theme} toastOptions={{ style: { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-main)' } }} />

      {/* Hero */}
      <section className="py-10 md:py-16 relative overflow-hidden border-b border-[var(--border)]">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-main relative z-10">
          <Link to="/services/live-sessions" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 font-mono text-sm">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Live Sessions
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <span className={`inline-block px-3 py-1 text-xs font-mono uppercase mb-4 ${
                session.type === 'Live Hack' ? 'bg-[var(--error)]/20 text-[var(--error)]' :
                session.type === 'Q&A' ? 'bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)]' :
                session.type === 'Tool Demo' ? 'bg-[var(--accent-purple)]/20 text-[var(--accent-purple)]' :
                session.type === 'CTF' ? 'bg-[#FFD700]/20 text-[#FFD700]' :
                'bg-[var(--primary)]/20 text-[var(--primary)]'
              }`}>{session.type}</span>
              <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)] mb-6">{session.title}</h1>
              <p className="text-[var(--text-muted)] text-lg leading-relaxed mb-6">{details.summary}</p>
              <div className="flex flex-wrap gap-2">
                {session.tags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 bg-[var(--primary)]/10 border border-[var(--primary)]/30 text-sm text-[var(--primary)] font-mono">{tag}</span>
                ))}
              </div>
            </div>
            <div className="bg-[var(--surface)] border border-[var(--border)] p-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3"><Calendar className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Date</p><p className="text-[var(--text-main)] font-mono">{session.date}</p></div></div>
                <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Time</p><p className="text-[var(--text-main)] font-mono">{session.time}</p></div></div>
                <div className="flex items-center gap-3"><Video className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Duration</p><p className="text-[var(--text-main)] font-mono">{session.duration}</p></div></div>
              </div>
              <div className="border-t border-[var(--border)] pt-6">
                <p className="font-mono text-2xl font-bold text-[var(--primary)] mb-2">FREE</p>
                <p className="text-xs text-[var(--text-muted)] mb-4">Open to all</p>
                <a href="https://form.jotform.com/261171526591053" target="_blank" rel="noopener noreferrer" className="w-full bg-[var(--primary)] text-black font-mono font-bold px-4 py-3 text-sm uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 text-center block">Register Now</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Host */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-6">./Guest_speaker</p>
          <div className="space-y-8">
            {(session.speakers || [{ name: session.host }]).map((speaker, idx) => (
              <div key={idx} className="flex items-start gap-6">
                {speaker.photo ? (
                  <img
                    src={speaker.photo}
                    alt={speaker.name}
                    className="w-20 h-20 rounded-full object-cover border border-[var(--primary)] flex-shrink-0"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="w-20 h-20 bg-[var(--surface-highlight)] border border-[var(--primary)] flex items-center justify-center font-mono text-[var(--primary)] text-2xl font-bold flex-shrink-0">
                    {speaker.name.split(' ').map(n => n[0]).join('')}
                  </div>
                )}
                <div>
                  <h3 className="text-2xl font-bold text-[var(--text-main)] mb-1">{speaker.name}</h3>
                  {(speaker.designation || speaker.company) && (
                    <p className="text-[var(--primary)] text-sm font-mono mb-2">
                      {speaker.designation && speaker.company
                        ? `${speaker.designation} @ ${speaker.company}`
                        : speaker.designation || speaker.company}
                    </p>
                  )}
                  {speaker.bio && <p className="text-[var(--text-muted)] leading-relaxed">{speaker.bio}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-6">./what_we_will_cover</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {details.topics.map((topic, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-[var(--surface)] border border-[var(--border)]">
                <CheckCircle className="w-5 h-5 text-[var(--primary)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-main)]">{topic}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who is this for */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-6">./who_should_attend</p>
          <ul className="space-y-3">
            {details.suitableFor.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <User className="w-5 h-5 text-[var(--accent-cyan)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-main)]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-6">./what_you_get</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {details.benefits.map((benefit, idx) => (
              <div key={idx} className="p-4 bg-[var(--surface)] border border-[var(--border)]">
                <CheckCircle className="w-6 h-6 text-[var(--primary)] mb-3" />
                <p className="text-[var(--text-main)]">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Register CTA */}
      <section className="py-8 md:py-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="container-main relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-main)] mb-4">Don't Miss This Session!</h2>
          <p className="text-[var(--text-muted)] text-lg max-w-2xl mx-auto mb-8">
            {session.maxAttendees - session.attendees} spots remaining. Register now to secure your place.
          </p>
          <a href="https://form.jotform.com/261171526591053" target="_blank" rel="noopener noreferrer" className="bg-[var(--primary)] text-black font-mono font-bold px-12 py-4 text-lg uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_25px_var(--glow-primary)] inline-block" data-testid="register-btn">Register Free</a>
        </div>
      </section>
    </div>
  );
};

export default SessionDetailPage;
